import { useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  Checkbox,
  DatePicker,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
} from "../../../../components";
import { value } from "../../../../helpers";
import { useFiltrosRelatoriosHandlers } from "../../components/use-filtros-relatorios-handlers";
import {
  GrupoServicoService,
  PessoaService,
  ServicoService,
} from "../../../../services";
import { responseErros, timeoutBuscaAutocomplete } from "../../../../utils";

const pessoaService = new PessoaService();
const grupoServicoService = new GrupoServicoService();
const servicoService = new ServicoService();

const FormFiltroAvancadoAnaliseAbcServicos = ({
  filtrosAvancados,
  setFiltrosAvancados,
  setFiltrosAplicadosImpressao,
  setValidationError,
}) => {
  const inputRefCliente = useRef();
  const inputRefServico = useRef();

  const [clienteList, setClienteList] = useState([]);
  const [servicoList, setServicoList] = useState([]);
  const [grupoServicoList, setGrupoServicoList] = useState([]);
  const [prestadorList, setPrestadorList] = useState([]);
  const [loadingAutoComplete, setLoadingAutoComplete] = useState(false);

  const { onChangeInput, onChangeDate, onChangeCheckbox, onChangeAutocomplete } =
    useFiltrosRelatoriosHandlers(
      setFiltrosAvancados,
      setFiltrosAplicadosImpressao,
    );

  useEffect(() => {
    setFiltrosAvancados((prev) => ({
      ...prev,
      classeA: 70,
      classeB: 20,
      classeC: 10,
      situacaoOs: "AMBAS",
    }));
    buscarGruposServico();
    buscarPrestadores();
  }, []);

  const handleChangeSituacaoOs = (e) => {
    const val = e.target.value;
    setFiltrosAvancados((prev) => ({ ...prev, situacaoOs: val }));
    const labels = { ABERTA: "Aberta", FECHADA: "Fechada", AMBAS: "Ambas" };
    setFiltrosAplicadosImpressao((prev) => ({
      ...prev,
      "Situação-OS": labels[val] ?? val,
    }));
  };

  const buscarGruposServico = () => {
    grupoServicoService.getAll({ nonPaginated: true }).then((result) => {
      if (!result.isAxiosError) {
        setGrupoServicoList(result.data?.rows ?? result.data ?? []);
      } else {
        responseErros(result);
      }
    });
  };

  const buscarPrestadores = () => {
    pessoaService
      .getAll({ nonPaginated: true, tiposPessoaId: 5 })
      .then((result) => {
        if (!result.isAxiosError) {
          setPrestadorList(result.data);
        } else {
          responseErros(result);
        }
      });
  };

  const buscarServicos = (filtros) => {
    servicoService
      .getAll({ nonPaginated: true, ...filtros })
      .then((result) => {
        if (!result.isAxiosError) {
          setServicoList(result.data?.rows ?? result.data ?? []);
        } else {
          responseErros(result);
        }
      });
    setLoadingAutoComplete(false);
  };

  const buscarClientes = (filtros) => {
    pessoaService
      .getAll({ nonPaginated: true, tiposPessoaId: 1, ...filtros })
      .then((result) => {
        if (!result.isAxiosError) {
          setClienteList(result.data);
        } else {
          responseErros(result);
        }
      });
    setLoadingAutoComplete(false);
  };

  const onInputChangeServico = (event) => {
    if (
      event.key === "Enter" &&
      inputRefServico?.current === document.activeElement &&
      event.target?.value?.length > 2
    ) {
      setLoadingAutoComplete(true);
      timeoutBuscaAutocomplete(
        buscarServicos({ descricao: event.target.value }),
      );
    }
  };

  const onInputChangeCliente = (event) => {
    if (
      event.key === "Enter" &&
      inputRefCliente?.current === document.activeElement &&
      event.target?.value?.length > 2
    ) {
      setLoadingAutoComplete(true);
      const filtros = {
        nomeRazaoSocial: event.target.value,
        apelidoFantasia: event.target.value,
      };
      timeoutBuscaAutocomplete(buscarClientes(filtros));
    }
  };

  const handleChangeClasse = (e, field) => {
    const novoValor = Number(e.target.value) || 0;
    const classeA =
      field === "classeA" ? novoValor : Number(filtrosAvancados?.classeA || 0);
    const classeB =
      field === "classeB" ? novoValor : Number(filtrosAvancados?.classeB || 0);
    const classeC =
      field === "classeC" ? novoValor : Number(filtrosAvancados?.classeC || 0);
    if (classeA + classeB + classeC > 100) return;
    onChangeInput(e, field, "number");
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <FormControl variant="outlined" margin="normal" fullWidth>
            <Autocomplete
              id="servicoId"
              name="Serviço"
              options={servicoList}
              autoHighlight
              disablePortal
              getOptionLabel={(option) => option?.descricao ?? ""}
              getOptionSelected={(option, val) =>
                option?.descricao === val?.descricao
              }
              loading={loadingAutoComplete}
              loadingText="Carregando"
              noOptionsText="Digite e pressione Enter"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Serviço"
                  variant="outlined"
                  onKeyDown={onInputChangeServico}
                  inputRef={inputRefServico}
                  InputProps={{ ...params.InputProps }}
                />
              )}
              onChange={(e, val) =>
                onChangeAutocomplete("servicoId", val, "Serviço")
              }
              value={value.autoComplete(servicoList, filtrosAvancados?.servicoId)}
            />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl variant="outlined" margin="normal" fullWidth>
            <Autocomplete
              id="grupoServicoId"
              name="Grupo-Serviço"
              options={grupoServicoList}
              autoHighlight
              disablePortal
              getOptionLabel={(option) => option?.descricao ?? ""}
              getOptionSelected={(option, val) => option?.id === val?.id}
              noOptionsText="Sem opções"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Grupo de Serviço"
                  variant="outlined"
                />
              )}
              onChange={(e, val) =>
                onChangeAutocomplete("grupoServicoId", val, "Grupo-Serviço")
              }
              value={value.autoComplete(
                grupoServicoList,
                filtrosAvancados?.grupoServicoId,
              )}
            />
          </FormControl>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <FormControl variant="outlined" margin="normal" fullWidth>
            <Autocomplete
              id="clienteId"
              name="Cliente"
              options={clienteList}
              autoHighlight
              disablePortal
              getOptionLabel={(option) => option?.nomeRazaoSocial ?? ""}
              getOptionSelected={(option, val) =>
                option?.nomeRazaoSocial === val?.nomeRazaoSocial
              }
              loading={loadingAutoComplete}
              loadingText="Carregando"
              noOptionsText="Digite e pressione Enter"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Cliente"
                  variant="outlined"
                  onKeyDown={onInputChangeCliente}
                  inputRef={inputRefCliente}
                  InputProps={{ ...params.InputProps }}
                />
              )}
              onChange={(e, val) =>
                onChangeAutocomplete("clienteId", val, "Cliente")
              }
              value={value.autoComplete(clienteList, filtrosAvancados?.clienteId)}
            />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl variant="outlined" margin="normal" fullWidth>
            <Autocomplete
              id="prestadorId"
              name="Prestador"
              options={prestadorList}
              autoHighlight
              disablePortal
              getOptionLabel={(option) => option?.nomeRazaoSocial ?? ""}
              getOptionSelected={(option, val) =>
                option?.nomeRazaoSocial === val?.nomeRazaoSocial
              }
              noOptionsText="Sem opções"
              renderInput={(params) => (
                <TextField {...params} label="Prestador" variant="outlined" />
              )}
              onChange={(e, val) =>
                onChangeAutocomplete("prestadorId", val, "Prestador")
              }
              value={value.autoComplete(
                prestadorList,
                filtrosAvancados?.prestadorId,
              )}
            />
          </FormControl>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <DatePicker
            id="dataStart"
            name="Data-Inicial"
            label="Data Inicial"
            margin
            format="dd/MM/yyyy"
            value={value.date(filtrosAvancados?.dataStart)}
            onChange={(date) =>
              onChangeDate("dataStart", date, "Data-Inicial")
            }
            slotProps={{ popper: { sx: { zIndex: 2000 } } }}
            setError={setValidationError}
          />
        </Grid>
        <Grid item xs={6}>
          <DatePicker
            id="dataEnd"
            name="Data-Final"
            label="Data Final"
            margin
            format="dd/MM/yyyy"
            value={value.date(filtrosAvancados?.dataEnd)}
            onChange={(date) =>
              onChangeDate("dataEnd", date, "Data-Final")
            }
            slotProps={{ popper: { sx: { zIndex: 2000 } } }}
            setError={setValidationError}
          />
        </Grid>
      </Grid>

      <Divider sx={{ mt: 2, mb: 1, border: "1px solid grey" }} />

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControl className="d-flex justify-content-center align-items-center p-2">
            <RadioGroup
              row
              aria-labelledby="situacao-os-label"
              name="situacaoOs"
              value={filtrosAvancados?.situacaoOs || "AMBAS"}
              onChange={handleChangeSituacaoOs}
            >
              <FormControlLabel
                value="ABERTA"
                control={<Radio />}
                label="Abertas"
              />
              <FormControlLabel
                value="FECHADA"
                control={<Radio sx={{ ml: "1rem" }} />}
                label="Fechadas"
              />
              <FormControlLabel
                value="AMBAS"
                control={<Radio sx={{ ml: "1rem" }} />}
                label="Ambas"
              />
            </RadioGroup>
          </FormControl>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Select
            id="ordenarPor"
            name="ordenarPor"
            margin="normal"
            value={value.text(filtrosAvancados?.ordenarPor)}
            label="Ordenar Por"
            onChange={(event) => onChangeInput(event, "ordenarPor")}
            fullWidth
            SelectProps={{
              MenuProps: {
                disablePortal: true,
                PaperProps: { sx: { zIndex: 1600 } },
              },
            }}
          >
            <MenuItem value="NUMERICA">Numérica</MenuItem>
            <MenuItem value="ALFABETICA">Alfabética</MenuItem>
            <MenuItem value="VALOR_ACUMULADO">Valor Acumulado</MenuItem>
          </Select>
        </Grid>
      </Grid>

      <Divider sx={{ mt: 2, mb: 1, border: "1px solid grey" }} />

      <Grid container spacing={2}>
        <Grid item xs={4}>
          <FormControlLabel
            sx={{ mx: 0, mt: 2 }}
            label="A"
            labelPlacement="start"
            control={
              <TextField
                id="classeA"
                name="Classe-A"
                variant="outlined"
                sx={{ ml: 1, mr: 2 }}
                type="number"
                fullWidth
                value={value.text(filtrosAvancados?.classeA)}
                onChange={(e) => handleChangeClasse(e, "classeA")}
              />
            }
          />
        </Grid>
        <Grid item xs={4}>
          <FormControlLabel
            sx={{ mx: 0, mt: 2 }}
            label="B"
            labelPlacement="start"
            control={
              <TextField
                id="classeB"
                name="Classe-B"
                variant="outlined"
                sx={{ ml: 1, mr: 2 }}
                type="number"
                fullWidth
                value={value.text(filtrosAvancados?.classeB)}
                onChange={(e) => handleChangeClasse(e, "classeB")}
              />
            }
          />
        </Grid>
        <Grid item xs={4}>
          <FormControlLabel
            sx={{ mx: 0, mt: 2 }}
            label="C"
            labelPlacement="start"
            control={
              <TextField
                id="classeC"
                name="Classe-C"
                variant="outlined"
                sx={{ ml: 1, mr: 2 }}
                type="number"
                fullWidth
                value={value.text(filtrosAvancados?.classeC)}
                onChange={(e) => handleChangeClasse(e, "classeC")}
              />
            }
          />
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            label="Apenas o que é gerado para Contas a Receber"
            labelPlacement="end"
            control={
              <Checkbox
                name="apenasContasReceber"
                checked={!!filtrosAvancados?.apenasContasReceber}
                onChange={(e) =>
                  onChangeCheckbox(e, "Apenas-Contas-Receber")
                }
                color="primary"
              />
            }
          />
        </Grid>
      </Grid>
    </>
  );
};

export default FormFiltroAvancadoAnaliseAbcServicos;
